import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, filter, map, Observable, of, switchMap, take, tap, timer} from 'rxjs';

import {CittadinoPushTokenService} from '../core/api/generated';
import {LocalStorageService} from './local-storage.service';

/**
 * Registrazione del push token FCM presso il backend.
 *
 * <p>Il token nasce nel guscio nativo (FCM su Android, FCM-via-APNs su iOS) e arriva alla web app
 * con `window.onPushTokenReceived(token)`, agganciato in `AppComponent`. Da qui va inoltrato al BE
 * con `PUT /api/v1/cittadino/push-token`, che lo associa al caller: lo user non si passa mai nel
 * body, lo ricava il BE dal Bearer.
 *
 * <p><b>Il nodo è il timing.</b> Token nativo e login sono eventi asincroni e indipendenti, in
 * ordine non garantito:
 * <ul>
 *   <li>il nativo consegna il token dopo `appInitEnd`, quindi può precedere il login manuale
 *       (registrarlo subito darebbe 401, e il token andrebbe perso);</li>
 *   <li>a sessione recuperata il token può invece arrivare dopo, quando l'utente è già dentro.</li>
 * </ul>
 * Perciò le due condizioni non si aspettano a vicenda: ognuna registra il token quando <i>entrambe</i>
 * sono soddisfatte, leggendo l'ultimo token noto da {@link ultimoToken}. È il motivo per cui il token
 * è tenuto in un BehaviorSubject invece di essere registrato al volo alla ricezione.
 *
 * <p>Il token è considerato dato di sessione, non un segreto d'identità: resta in memoria (come
 * `deviceId`), non in storage. Un riavvio della web app lo riottiene dal nativo, che lo tiene in
 * cache propria e lo riconsegna a ogni `appInitEnd`.
 */
@Injectable({
  providedIn: 'root'
})
export class PushTokenService {

  private readonly _ultimoToken$ = new BehaviorSubject<string | null>(null);

  /** Ultimo token consegnato dal guscio nativo in questa sessione, `null` se non ne è mai arrivato uno. */
  get ultimoToken(): string | null {
    return this._ultimoToken$.value;
  }

  constructor(
    private pushTokenApi: CittadinoPushTokenService,
    private localStorageService: LocalStorageService) {
  }

  /**
   * Riceve il token dal guscio nativo e lo registra se c'è già una sessione attiva.
   *
   * <p>Se l'utente non è ancora autenticato il token resta in attesa: sarà
   * {@link registraTokenPendente} — al login o al recovery — a inoltrarlo. Un token identico
   * all'ultimo già registrato non produce una nuova chiamata: il nativo lo riconsegna a ogni
   * `appInitEnd`, e l'upsert è idempotente ma non gratuito.
   */
  onTokenNativoRicevuto(token: string): void {
    if (!token) {
      return;
    }
    const giaNoto = this._ultimoToken$.value === token;
    this._ultimoToken$.next(token);

    if (giaNoto) {
      return;
    }
    if (!this.localStorageService.isLogged) {
      console.log('[PushToken] token ricevuto senza sessione attiva: registrazione differita al login');
      return;
    }
    this.registra$(token).subscribe();
  }

  /**
   * Registra il token già ricevuto dal nativo, se presente. Da chiamare quando la sessione diventa
   * attiva (login riuscito, recovery al bootstrap): copre il caso in cui il token è arrivato prima
   * dell'autenticazione.
   *
   * <p>Non emette errori: una registrazione push fallita non deve mai interrompere il flusso di
   * login del chiamante.
   */
  registraTokenPendente$(): Observable<void> {
    const token = this._ultimoToken$.value;
    if (!token) {
      return of(undefined);
    }
    return this.registra$(token);
  }

  /**
   * Rimuove dal BE il token di questo dispositivo.
   *
   * <p><b>Va chiamata prima di chiudere la sessione</b>: l'endpoint richiede il Bearer del caller,
   * quindi dopo la pulizia dei token risponderebbe 401. Il `deviceId` arriva dal guscio nativo
   * (`window.onDeviceIdReceived`); senza guscio nativo — app aperta nel browser — non c'è nulla da
   * rimuovere e la chiamata è un no-op.
   *
   * <p>Non emette errori: il logout deve procedere anche se la rimozione non va a buon fine. Il
   * token orfano lato BE verrà scartato al primo invio fallito.
   */
  rimuovi$(): Observable<void> {
    const idDevice = this.localStorageService.deviceId;
    if (!idDevice) {
      return of(undefined);
    }
    return this.pushTokenApi.deletePushTokenCittadino({idDevice}).pipe(
      tap(() => console.log('[PushToken] token rimosso dal backend')),
      map(() => undefined),
      catchError((err) => {
        console.warn('[PushToken] rimozione token non riuscita', err?.status ?? err);
        return of(undefined);
      }),
    );
  }

  /** Azzera lo stato locale: il token della sessione precedente non va riusato per il prossimo utente. */
  reset(): void {
    this._ultimoToken$.next(null);
  }

  /**
   * Attende che il `deviceId` sia disponibile e inoltra il token al BE.
   *
   * <p>`deviceId` e token push arrivano dal nativo in momenti distinti (`onDeviceIdReceived` e
   * `onPushTokenReceived`), quindi l'id può non essere ancora arrivato. Si attende con un poll
   * breve invece di rinunciare subito: `id_device` è obbligatorio nel form e senza di esso il BE
   * non potrebbe distinguere i dispositivi dello stesso utente.
   */
  private registra$(token: string): Observable<void> {
    return this.attendiDeviceId$().pipe(
      switchMap((idDevice) => {
        if (!idDevice) {
          console.log('[PushToken] deviceId non disponibile: registrazione saltata');
          return of(undefined);
        }
        return this.pushTokenApi.upsertPushTokenCittadino({
          pushTokenUpsertForm: {id_device: idDevice, token},
        }).pipe(
          tap(() => console.log('[PushToken] token registrato sul backend')),
          map(() => undefined),
          catchError((err) => {
            // 400 = token rifiutato da Firebase (tipico con credenziali BE non configurate).
            console.warn('[PushToken] registrazione token non riuscita', err?.status ?? err);
            return of(undefined);
          }),
        );
      }),
    );
  }

  /**
   * Risolve col `deviceId` appena disponibile, o con `null` se non arriva entro la finestra di
   * attesa (caso normale fuori dal guscio nativo, dove non arriverà mai).
   */
  private attendiDeviceId$(): Observable<string | null> {
    if (this.localStorageService.deviceId) {
      return of(this.localStorageService.deviceId);
    }
    return timer(0, DEVICE_ID_POLL_MS).pipe(
      map(() => this.localStorageService.deviceId),
      filter((id, index) => !!id || index >= DEVICE_ID_MAX_TENTATIVI),
      take(1),
      map((id) => id ?? null),
    );
  }
}

const DEVICE_ID_POLL_MS = 250;
const DEVICE_ID_MAX_TENTATIVI = 20; // ~5s: oltre, il guscio nativo non lo consegnerà.
